/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {Sprite, Texture} from 'pixi.js';
import {ITrackEntry, IEvent} from 'pixi-spine';
import {EmitterV5, Meter, Timer} from '@gongxigames/framework/core';
import {BigWinConstants} from '@gongxigames/framework/slot';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {KoalaBigWinConstants} from '@gongxigames/framework/slotV2/modules-koala/bigwin/KoalaBigWinConstants';
import {$RichText, getTexture} from '@gongxigames/framework/core/fragments/BuilderY';
import {BigWinViewV2} from '@gongxigames/framework/slotV2/modules/bigwin/BigWinViewV2';
import {RichText} from '@gongxigames/framework/core/fragments/display';

export class KoalaBigWinView extends BigWinViewV2 {
    private _meters: { [key: string]: Meter; } = {};
    private _particlesTimers: { [key: string]: Timer; } = {};
    private _emitters: { [key: string]: EmitterV5; } = {};
    private _shouldPlayBigWinLoop = false;
    private _shouldPlayBigWinEnd = false;
    private _meterEasing: Function = (k: number) => Math.pow(k, 1.5);

    public onResourcesLoaded(): void {
        this.initSpineAnimation();
        this.showPopup.value = false;
    }

    public override skip(): void {
        super.skip();

        if (!this._isSkippableImmediately && this._canSkip) {
            this.rewindSpine();
        }
    }

    protected playBigWinStartSound(): void {
        const bWinSound = slotStore.soundManager.value!.getSound(BigWinConstants.MAIN_SOUND);

        if (bWinSound) {
            const duration = bWinSound.duration * 1000;

            this._shouldPlayBigWinLoop = true;

            this._updater.addTimer(() => {
                if (this._shouldPlayBigWinLoop) {
                    this._playingSound = KoalaBigWinConstants.LOOP_SOUND;
                    slotStore.soundPlayer.value!.play(KoalaBigWinConstants.LOOP_SOUND, true);
                }
            }, duration, true);
        }
    }

    protected startMeterFor(slotName: string, duration: number): void {
        const counterSlot = this._spine.skeleton.findSlot(slotName);

        if (!counterSlot.currentSprite) {
            return;
        }

        const counterText = <$RichText
            style={'koalaBigWinValue'}
            maxWidth={1800}
            text={'{{value}}'}
        /> as RichText;

        counterSlot.currentSprite.texture = Texture.EMPTY;
        counterSlot.currentSprite.removeChildren();
        counterSlot.currentSprite.addChild(counterText);

        const meter = new Meter(counterText, this._meterEasing, slotStore.currencyFormatter.value!);
        const offsetY = 20;

        meter.from(0).to(this._to - this._from, duration);
        meter.start();
        meter.onCompleteCallback = this.onCounterFinished.bind(this, slotName);
        this._updater.add(meter);
        meter.updateCallback = () => {
            counterText.x = -counterText.getLocalBounds(counterText.parent.getBounds()).width / 2;
            counterText.y = -counterText.getLocalBounds(counterText.parent.getBounds()).height / 2 - offsetY;
        };
        this._meters[slotName] = meter;
    }

    protected rewindSpine(): void {
        const currentTime = this._spine.state.tracks[0].trackTime;
        const duration = this._spine.state.tracks[0].animationEnd;

        const skipToBeforeEnd = duration - KoalaBigWinConstants.BIG_WIN_TIMINGS.skipToBeforeEnd[this._BWType] / 1000;

        if (currentTime < skipToBeforeEnd) {
            this._spine.state.tracks[0].trackTime = skipToBeforeEnd;
        }
    }

    protected override stop(): void {
        Object.values(this._meters).forEach((meter) => {
            meter.onCompleteCallback = undefined;
            meter.stop();
        });

        super.stop();
    }

    protected override setSkipOptions(): void {
        this._skipTimer = new Timer(() => this._canSkip = true, KoalaBigWinConstants.BIG_WIN_TIMINGS.skip_delay, true);
        this._updater.add(this._skipTimer);
    }

    protected override playBWAnimation(): void {
        this.playSpineAnimation();
        this.startCounter();
        this.startEmitParticles();
    }

    protected override startEmitParticles(): void {
        KoalaBigWinConstants.PARTICLES_SLOTS[this._BWType].slots.forEach((slotName, index) => {
            this._particlesTimers[slotName] = new Timer(() => {
                const sprite = this.addParticlesEmitterToSlot(slotName);

                this.emitParticles(slotName, sprite);
            }, KoalaBigWinConstants.PARTICLES_SLOTS[this._BWType].delay[index], true);
            this._updater.add(this._particlesTimers[slotName]);
        });
    }

    protected override startCounter(): void {
        const duration = KoalaBigWinConstants.BIG_WIN_TIMINGS.durationCounter[this._BWType] - KoalaBigWinConstants.BIG_WIN_TIMINGS.animation_delay;

        ['value_bigwin', 'value_mega', 'value_ultra'].forEach(value => this.startMeterFor(value, duration));

        // start flow meter
        if (this._isPlayWinMeter) {
            this.options.onMeterStarted();
        }
    }

    protected override onCounterFinished(slotName?: string): void {
        const meter = slotName && this._meters[slotName];

        if (meter) {
            meter.onCompleteCallback = undefined;
            meter.stop();
        }

        this.options.onMeterFinished();
    }

    protected override onBigWinAnimationCompleted(): void {
        this._isSkippableImmediately = true;

        this.stopEmitParticles();
        this.fade();

        slotStore.soundPlayer.value!.stop(KoalaBigWinConstants.LOOP_SOUND);

        if (this._shouldPlayBigWinEnd) {
            this._shouldPlayBigWinEnd = false;
            slotStore.soundPlayer.value!.play(BigWinConstants.END_SOUND);
        }
    }

    protected override setEndSound(): void {
        this._shouldPlayBigWinLoop = false;
        slotStore.soundPlayer.value!.stop(KoalaBigWinConstants.LOOP_SOUND);
        slotStore.soundPlayer.value!.stop(this._playingSound);

        if (this._shouldPlayBigWinEnd) {
            this._shouldPlayBigWinEnd = false;
            slotStore.soundPlayer.value!.play(BigWinConstants.END_SOUND);
        }
    }

    protected override addParticlesEmitterToSlot(slotName?: string): Sprite {
        const slotSprite = this._spine.skeleton.findSlot(slotName!).currentSprite;
        const sprite = new Sprite();

        sprite.anchor.set(0.5);

        slotSprite.texture = Texture.EMPTY;
        slotSprite.removeChildren();
        slotSprite.addChild(sprite);

        return sprite;
    }

    protected override playSpineAnimation(): void {
        this._spine.state.setAnimation(0, this._BWType, false);
        this._shouldPlayBigWinEnd = true;

        const eventCallback = (sound: string, onSoundEnd?: () => void) => {
            this._playingSound = sound;
            slotStore.soundPlayer.value!.play(this._playingSound, false, onSoundEnd);
        };

        const onAnimationEvent = (trackEntry: ITrackEntry, event: IEvent) => {
            switch (event.data.name) {
                case 'big_win_start':
                    eventCallback('bigwin');
                    this.playBigWinStartSound();
                    break;
                case 'mega_win_start':
                    eventCallback('megaWin');
                    break;
                case 'ultra_win_start':
                    eventCallback('ultraWin');
                    break;
                default:
                    break;
            }
        };

        this._spine.state.addListener({
            event: (trackEntry, event) => onAnimationEvent(trackEntry, event),
            complete: () => {
                this._spine.state.clearListeners();
                this.onBigWinAnimationCompleted();
            }
        });

        this._spine.update(0);
        this._spineContainer.visible = true;
    }

    protected override setCounterToFinalValue(): void {
        Object.values(this._meters).forEach((meter) => {
            meter.snapToFinalValue();
        });

        super.setCounterToFinalValue();
    }

    protected override emitParticles(slotName?: string, sprite?: Sprite): void {

        const textures = this.getParticlesTextures();
        const config = KoalaBigWinConstants.PARTICLES_CONFIG(textures);

        if (slotName && sprite) {
            const emitter = new EmitterV5(
                sprite,
                config
            );

            this._updater.add(emitter);
            this._emitters[slotName] = emitter;
            emitter.startEmit();
        }
    }

    protected override setBurstVisibility(value: boolean): void {
    }

    protected override stopEmitParticles(): void {
        this._emitters && Object.values(this._emitters).forEach((emitter) => {
            emitter.stopEmit();
        });
    }

    protected override stopEmitters(): void {
        this._emitters && Object.values(this._emitters).forEach((emitter) => {
            emitter.stopEmit();
        });
    }

    protected override clearTimers(): void {
        super.clearTimers();

        Object.values(this._particlesTimers).forEach((timer) => {
            timer.stop();
        });
    }

    protected override getParticlesTextures(): Texture[][] {
        const particlesTextures: Texture[] = [];
        let i = 1;
        let texture;

        while ((texture = getTexture(`3d_coin_${i}`))) {
            particlesTextures.push(texture);
            i++;
        }

        return [particlesTextures];
    }
}
